import React, { useEffect, useState } from "react";
import { api, getErr, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowSquareOut, DownloadSimple, Plus, Trash, MapPin } from "@phosphor-icons/react";

const Card = ({ title, children, testid, aside }) => (
  <div className="border border-[#E5E7EB] bg-white p-5" data-testid={testid}>
    <div className="flex items-start justify-between gap-3">
      <div className="font-display text-base font-black tracking-tight">{title}</div>
      {aside}
    </div>
    <div className="mt-4">{children}</div>
  </div>
);

const RESULT_LABEL = { core: "Core", maryland: "Maryland (non-core)", outside: "Outside MD" };
const RESULT_CLASS = { core: "bg-[#22C55E] text-white", maryland: "bg-[#F59E0B] text-white", outside: "bg-[#9CA3AF] text-white" };

export default function AdminServiceArea() {
  const [data, setData] = useState(null);
  const [groups, setGroups] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data: ov } = await api.get("/service-area/admin/overview");
      setData(ov);
      setGroups(ov.config.groups.map((g) => ({ ...g, zipsText: g.zips.join(", ") })));
    } catch (e) {
      toast.error(getErr(e));
    }
  };
  useEffect(() => { load(); }, []);

  const setG = (i, k, v) => setGroups((gs) => gs.map((g, j) => (j === i ? { ...g, [k]: v } : g)));
  const addGroup = () => setGroups((gs) => [...gs, { name: "", communities: "", zipsText: "" }]);
  const removeGroup = (i) => setGroups((gs) => gs.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { groups: groups.map((g) => ({ name: g.name, communities: g.communities, zips: [g.zipsText] })) };
      await api.put("/service-area/admin/config", payload);
      toast.success("Service area saved — live on /service-area now");
      await load();
    } catch (e) {
      toast.error(getErr(e));
    } finally {
      setSaving(false);
    }
  };

  if (!data || !groups) return <div className="p-6 text-sm text-[#6B7280]" data-testid="sa-admin-loading">Loading…</div>;
  const { stats } = data;
  const coreCount = groups.reduce((n, g) => n + g.zipsText.split(/[\s,;]+/).filter(Boolean).length, 0);

  return (
    <div className="space-y-6 p-6" data-testid="admin-service-area">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-black tracking-tight">Service Area</h1>
          <p className="mt-1 text-sm text-[#6B7280]">Core ZIP groups shown on /service-area and used by the ZIP checker. Changes are live immediately — no deploy.</p>
        </div>
        <a href="/service-area" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-bold text-[#0044FF]" data-testid="sa-admin-view-page">
          View page <ArrowSquareOut size={14} />
        </a>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          ["ZIPs checked", stats.total, "sa-stat-total"],
          ["Core", stats.by_result.core || 0, "sa-stat-core"],
          ["Maryland, non-core", stats.by_result.maryland || 0, "sa-stat-maryland"],
          ["Outside Maryland", stats.by_result.outside || 0, "sa-stat-outside"],
        ].map(([l, v, t]) => (
          <div key={l} className="border border-[#E5E7EB] bg-white p-4" data-testid={t}>
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#6B7280]">{l}</div>
            <div className="font-display mt-1 text-2xl font-black">{v}</div>
          </div>
        ))}
      </div>

      <Card
        title={`Core service area groups (${coreCount} ZIPs)`}
        testid="sa-groups-card"
        aside={<Button size="sm" variant="outline" onClick={addGroup} data-testid="sa-add-group"><Plus size={14} /> Add group</Button>}
      >
        <div className="space-y-4">
          {groups.map((g, i) => (
            <div key={i} className="grid grid-cols-1 gap-2 border border-[#E5E7EB] p-3 md:grid-cols-[1.2fr_1.8fr_1.4fr_auto]" data-testid={`sa-group-${i}`}>
              <div>
                <label className="text-[11px] font-bold text-[#6B7280]">Area group</label>
                <Input value={g.name} onChange={(e) => setG(i, "name", e.target.value)} placeholder="Catonsville & Southwest" data-testid={`sa-group-${i}-name`} />
              </div>
              <div>
                <label className="text-[11px] font-bold text-[#6B7280]">Communities</label>
                <Input value={g.communities} onChange={(e) => setG(i, "communities", e.target.value)} placeholder="Catonsville, Halethorpe, Arbutus" data-testid={`sa-group-${i}-communities`} />
              </div>
              <div>
                <label className="text-[11px] font-bold text-[#6B7280]">ZIP codes (comma-separated)</label>
                <Input value={g.zipsText} onChange={(e) => setG(i, "zipsText", e.target.value)} placeholder="21228, 21227" data-testid={`sa-group-${i}-zips`} />
              </div>
              <div className="flex items-end">
                <Button variant="ghost" size="sm" onClick={() => removeGroup(i)} aria-label="Remove group" data-testid={`sa-group-${i}-remove`}><Trash size={16} /></Button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Button onClick={save} disabled={saving} data-testid="sa-save">{saving ? "Saving…" : "Save service area"}</Button>
          {data.config.updated_at && <span className="text-xs text-[#6B7280]">Last saved {new Date(data.config.updated_at).toLocaleString()}</span>}
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title="Demand outside the core (top ZIPs)" testid="sa-top-non-core">
          {stats.top_non_core.length === 0 ? (
            <div className="text-sm text-[#6B7280]">No non-core ZIPs checked yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]"><th className="py-1">ZIP</th><th>Result</th><th className="text-right">Checks</th></tr></thead>
              <tbody>
                {stats.top_non_core.map((r) => (
                  <tr key={r.zip} className="border-t border-[#F3F4F6]" data-testid={`sa-top-zip-${r.zip}`}>
                    <td className="py-1.5 font-mono font-bold">{r.zip}</td>
                    <td><span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-bold ${RESULT_CLASS[r.result]}`}>{RESULT_LABEL[r.result]}</span></td>
                    <td className="text-right font-bold">{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card
          title="Recent ZIP checks"
          testid="sa-recent"
          aside={
            <a href={`${BACKEND_URL}/api/service-area/admin/log.csv`} className="inline-flex items-center gap-1 text-sm font-bold text-[#0044FF]" data-testid="sa-export-csv">
              <DownloadSimple size={14} /> Export CSV
            </a>
          }
        >
          {data.recent.length === 0 ? (
            <div className="text-sm text-[#6B7280]">Nothing logged yet.</div>
          ) : (
            <div className="max-h-[420px] overflow-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]"><th className="py-1">When</th><th>ZIP</th><th>Result</th></tr></thead>
                <tbody>
                  {data.recent.map((r) => (
                    <tr key={r.id} className="border-t border-[#F3F4F6]" data-testid="sa-recent-row">
                      <td className="py-1.5 text-xs text-[#6B7280]">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="font-mono font-bold"><MapPin size={12} className="mr-1 inline" />{r.zip}</td>
                      <td><span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-bold ${RESULT_CLASS[r.result]}`}>{RESULT_LABEL[r.result]}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
